import {Component, inject, Input, OnInit} from '@angular/core';
import {ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {DatePipe} from '@angular/common';
import {NgSelectModule} from '@ng-select/ng-select';
import {CustomToastService} from '../../@core/utils/custom-toast.service';
import {GeschwisterService} from '../../@core/services/student-ticket/geschwister.service';
import {SchuelerService} from '../../@core/services/schueler/schueler.service';
import {SchuelerDTO} from '../../models/schueler/SchuelerDTO';
import {GeschwisterkindDto} from '../../models/geschwister/GeschwisterkindDto';

export interface GeschwisterFormData {
  geschwisterkind1Id: number | null;
  geschwisterkind2Id: number | null;
  geschwisterkind3Id: number | null;
  geschwisterkind4Id: number | null;
  geschwisterkind5Id: number | null;
}

/**
 * Wiederverwendbare Geschwister-Formularlogik - eingebettet sowohl im Verifizierungs-Wizard
 * (GeschwisterStepComponent) als auch im GeschwisterAdjustModalComponent.
 */
@Component({
  selector: 'app-geschwister-details-form',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, TranslatePipe],
  templateUrl: './geschwister-details-form.component.html',
})
export class GeschwisterDetailsFormComponent implements OnInit {

  @Input() ticketId: string;
  @Input() initialData: GeschwisterFormData | null = null;
  // Erlaubt unterschiedliche data-cy-Präfixe je nach Einbettungskontext (Wizard-Step vs. Modal)
  @Input() testIdPrefix = 'geschwister-select-';
  // Erlaubt unterschiedliche id/for-Präfixe je nach Einbettungskontext (Wizard-Step vs. Modal)
  @Input() idPrefix = 'geschwisterkind';

  form: UntypedFormGroup;
  protected schuelerList: { id: number; ganzerNameGeburtsdatum: string }[] = [];
  protected geschwisterSlots = [1, 2, 3, 4, 5];

  private readonly fb = inject(UntypedFormBuilder);
  private readonly geschwisterService = inject(GeschwisterService);
  private readonly schuelerService = inject(SchuelerService);
  private readonly customToastService = inject(CustomToastService);
  private readonly translateService = inject(TranslateService);
  private readonly datePipe = new DatePipe('de-DE');

  ngOnInit(): void {
    this.form = this.fb.group({
      geschwisterkind1: [null],
      geschwisterkind2: [null],
      geschwisterkind3: [null],
      geschwisterkind4: [null],
      geschwisterkind5: [null]
    });

    this.loadSchuelerList();

    if (this.initialData) {
      this.form.patchValue({
        geschwisterkind1: this.initialData.geschwisterkind1Id,
        geschwisterkind2: this.initialData.geschwisterkind2Id,
        geschwisterkind3: this.initialData.geschwisterkind3Id,
        geschwisterkind4: this.initialData.geschwisterkind4Id,
        geschwisterkind5: this.initialData.geschwisterkind5Id,
      });
    } else {
      this.loadCurrentGeschwister();
    }
  }

  private loadSchuelerList(): void {
    this.schuelerService.getSchuelerList().subscribe({
      next: (schueler: SchuelerDTO[]) => {
        this.schuelerList = schueler.map(s => ({
          id: s.id,
          ganzerNameGeburtsdatum: s.vorname + ' ' + s.name +
            ' (' + (s.geburtsdatum ? this.datePipe.transform(s.geburtsdatum, 'dd.MM.yyyy') : '—') + ')'
        }));
      },
      error: () => {
        this.customToastService.showError(
          this.translateService.instant('TICKETS.GESCHWISTER.LOAD_GESCHWISTER_LIST_ERROR')
        );
      }
    });
  }

  private loadCurrentGeschwister(): void {
    if (!this.ticketId) return;

    this.geschwisterService.getGeschwisterkinder(this.ticketId).subscribe({
      next: (geschwister: GeschwisterkindDto[]) => {
        this.addGeschwisterToSchuelerList(geschwister);
        for (const g of geschwister) {
          const controlName = 'geschwisterkind' + g.index;
          this.form.get(controlName)?.patchValue(g.data?.id ?? null);
        }
        this.form.markAsPristine();
      },
      error: () => {
        // Backend-Stub returns empty list — that's fine
      }
    });
  }

  private addGeschwisterToSchuelerList(geschwister: GeschwisterkindDto[]): void {
    for (const g of geschwister) {
      if (g.data && !this.schuelerList.find(s => s.id === g.data.id)) {
        this.schuelerList = [
          ...this.schuelerList,
          {
            id: g.data.id,
            ganzerNameGeburtsdatum: g.data.vorname + ' ' + g.data.name +
              ' (' + (g.data.geburtsdatum ? this.datePipe.transform(g.data.geburtsdatum, 'dd.MM.yyyy') : '—') + ')'
          }
        ];
      }
    }
  }

  protected onGeschwisterkindChange(nr: number): void {
    if (this.isDuplicate(nr)) {
      this.form.get('geschwisterkind' + nr)?.patchValue(null);
      this.customToastService.showError(
        this.translateService.instant('TICKETS.GESCHWISTER.DUPLICATE_ERROR')
      );
    }
  }

  private isDuplicate(nr: number): boolean {
    const selectedId = this.form.get('geschwisterkind' + nr)?.value;
    if (!selectedId) return false;

    return this.geschwisterSlots
      .filter(slot => slot !== nr)
      .some(slot => this.form.get('geschwisterkind' + slot)?.value === selectedId);
  }

  private getSelectedIds(): number[] {
    return this.geschwisterSlots
      .map(nr => this.form.get('geschwisterkind' + nr)?.value)
      .filter(id => id != null);
  }

  isValid(): boolean {
    return this.form.valid;
  }

  getFormValue(): GeschwisterFormData {
    const ids = this.getSelectedIds();
    return {
      geschwisterkind1Id: ids[0] ?? null,
      geschwisterkind2Id: ids[1] ?? null,
      geschwisterkind3Id: ids[2] ?? null,
      geschwisterkind4Id: ids[3] ?? null,
      geschwisterkind5Id: ids[4] ?? null,
    };
  }
}
